import { ActionIcon, Box, Button, Select, Table, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { calcLine, formatMoney } from '@erp/shared';
import type { Nomenclature } from '../api/nomenclature';

export interface IncomingUpdLineRow {
  key: string;
  nomenclatureId: string | null;
  name: string;
  unitSymbol: string;
  quantity: string;
  price: string;
  vatRateValue: string;
}

export function emptyIncomingUpdLineRow(): IncomingUpdLineRow {
  return {
    key: crypto.randomUUID(),
    nomenclatureId: null,
    name: '',
    unitSymbol: '',
    quantity: '1',
    price: '',
    vatRateValue: '',
  };
}

interface Props {
  lines: IncomingUpdLineRow[];
  onChange: (lines: IncomingUpdLineRow[]) => void;
  canEdit: boolean;
  nomenclatureOptions: Nomenclature[];
}

/** Ставка приходит текстом от поставщика («22%», «без НДС») — извлекаем число только для предпросмотра сумм. */
function parseVatRatePercent(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed || /без\s*ндс/i.test(trimmed)) return null;
  const match = trimmed.match(/-?\d+(?:[.,]\d+)?/);
  return match ? Number(match[0].replace(',', '.')) : null;
}

function calcRowAmounts(row: IncomingUpdLineRow) {
  const quantity = row.quantity.trim().replace(',', '.');
  const price = row.price.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(quantity) || !/^\d+(\.\d+)?$/.test(price)) {
    return { amount: '0.00', vatAmount: '0.00', totalAmount: '0.00' };
  }
  const amounts = calcLine({ quantity, price, vatRateValue: parseVatRatePercent(row.vatRateValue) });
  return {
    amount: amounts.amount.toFixed(2),
    vatAmount: amounts.vatAmount.toFixed(2),
    totalAmount: amounts.totalAmount.toFixed(2),
  };
}

export function IncomingUpdLinesEditor({ lines, onChange, canEdit, nomenclatureOptions }: Props) {
  const nomenclatureSelectOptions = nomenclatureOptions.map((n) => ({ value: n.id, label: n.name }));

  function updateRow(key: string, patch: Partial<IncomingUpdLineRow>) {
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function pickNomenclature(key: string, nomenclatureId: string | null) {
    if (!nomenclatureId) {
      updateRow(key, { nomenclatureId: null });
      return;
    }
    // Наименование и остальные поля остаются как в присланном документе — сопоставление
    // с номенклатурой только для узнаваемости, не для переноса наших цен/ставок на чужой документ.
    updateRow(key, { nomenclatureId });
  }

  function addRow() {
    onChange([...lines, emptyIncomingUpdLineRow()]);
  }

  function removeRow(key: string) {
    onChange(lines.filter((l) => l.key !== key));
  }

  const totals = lines.reduce(
    (acc, row) => {
      const a = calcRowAmounts(row);
      return {
        amount: acc.amount + Number(a.amount),
        vatAmount: acc.vatAmount + Number(a.vatAmount),
        totalAmount: acc.totalAmount + Number(a.totalAmount),
      };
    },
    { amount: 0, vatAmount: 0, totalAmount: 0 },
  );

  return (
    <Box>
      <Box style={{ overflowX: 'auto' }}>
        <Table verticalSpacing={6} miw={1050}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>№</Table.Th>
              <Table.Th w={200}>Сопоставить с номенклатурой</Table.Th>
              <Table.Th>Наименование</Table.Th>
              <Table.Th w={90}>Ед.</Table.Th>
              <Table.Th w={100}>Кол-во</Table.Th>
              <Table.Th w={120}>Цена</Table.Th>
              <Table.Th w={120}>Ставка НДС</Table.Th>
              <Table.Th w={110}>Сумма</Table.Th>
              <Table.Th w={100}>НДС</Table.Th>
              <Table.Th w={110}>Всего</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((row, index) => {
              const amounts = calcRowAmounts(row);
              return (
                <Table.Tr key={row.key}>
                  <Table.Td>{index + 1}</Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      placeholder="Не сопоставлено"
                      data={nomenclatureSelectOptions}
                      value={row.nomenclatureId}
                      onChange={(v) => pickNomenclature(row.key, v)}
                      disabled={!canEdit}
                      searchable
                      clearable
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.name}
                      onChange={(e) => updateRow(row.key, { name: e.currentTarget.value })}
                      disabled={!canEdit}
                      required
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="шт"
                      value={row.unitSymbol}
                      onChange={(e) => updateRow(row.key, { unitSymbol: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.quantity}
                      onChange={(e) => updateRow(row.key, { quantity: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.price}
                      onChange={(e) => updateRow(row.key, { price: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="22%"
                      value={row.vatRateValue}
                      onChange={(e) => updateRow(row.key, { vatRateValue: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatMoney(amounts.amount)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatMoney(amounts.vatAmount)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" fw={500}>
                      {formatMoney(amounts.totalAmount)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {canEdit && (
                      <ActionIcon variant="subtle" color="red" onClick={() => removeRow(row.key)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>

      {canEdit && (
        <Button mt="xs" size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addRow}>
          Добавить строку
        </Button>
      )}

      <Box mt="sm" ta="right">
        <Text size="sm">Сумма без НДС: {formatMoney(totals.amount)}</Text>
        <Text size="sm">НДС: {formatMoney(totals.vatAmount)}</Text>
        <Text size="md" fw={700}>
          Итого: {formatMoney(totals.totalAmount)}
        </Text>
      </Box>
    </Box>
  );
}
